/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useManualTopupMutation } from '@/hooks/usePlatformOwnerDetail'
import { ToLocaleStringFunc } from '@/lib/utils'

const schema = z.object({
  qty: z.number().int().min(1).optional(),
  packCode: z.string().trim().optional(),
  amountCfa: z.number(),
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL']),
  paidAt: z.string().trim().optional(),
  note: z.string().trim().optional(),
})
type FormValues = z.input<typeof schema>
type SubmitValues = z.output<typeof schema>

export function ManualTopupCard({ ownerId }: { ownerId: string }) {
  const mut = useManualTopupMutation(ownerId)

  const form = useForm<FormValues, any, SubmitValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      qty: 100,
      packCode: '',
      amountCfa: 0,
      channel: 'SMS',
      paidAt: new Date().toISOString().slice(0, 16),
      note: '',
    },
  })

  const submit = async (v: FormValues) => {
    try {
      const res = await mut.mutateAsync({
        qty: v.qty,
        packCode: v.packCode?.trim() || undefined,
        amountCfa: v.amountCfa === 0 ? undefined : Number(v.amountCfa),
        channel: v.channel,
        paidAt: v.paidAt ? new Date(v.paidAt).toISOString() : undefined,
        note: v.note?.trim() || undefined,
      })
      toast.success('Topup OK', {
        description: `periodKey: ${res.periodKey} • channel: ${res.channel}`,
      })

      form.reset({
        qty: 100,
        packCode: '',
        amountCfa: 0,
        channel: 'SMS',
        paidAt: new Date().toISOString().slice(0, 16),
        note: '',
      })
    } catch (e: any) {
      toast.error('Topup impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <h3 className='text-lg font-semibold'>Manual messages topup</h3>
        <p className='text-sm text-muted-foreground'>
          Crédit manuel de messages. Le periodKey est optionnel et peut être
          calculé côté backend.
        </p>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='qty'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Qty</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Qty'
                      value={ToLocaleStringFunc(field.value!)}
                      onChange={(event) => {
                        const rawValue = event.target.value.replace(/\s/g, '')
                        if (/^\d*$/.test(rawValue)) {
                          field.onChange(Number(rawValue))
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='channel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Channel</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='WHATSAPP'>WHATSAPP</SelectItem>
                      <SelectItem value='SMS'>SMS</SelectItem>
                      <SelectItem value='EMAIL'>EMAIL</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='amountCfa'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Montant CFA (optionnel)</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Montant CFA (optionel)'
                      value={ToLocaleStringFunc(field.value!)}
                      onChange={(event) => {
                        const rawValue = event.target.value.replace(/\s/g, '')
                        if (/^\d*$/.test(rawValue)) {
                          field.onChange(Number(rawValue))
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='paidAt'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Paid at (optionnel)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='2026-03-06T12:00:00.000Z' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='note'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Dépôt Wave / cash / etc.' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end'>
              <Button type='submit' disabled={mut.isPending}>
                {mut.isPending ? 'Topup…' : 'Appliquer'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
